import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { TOAST } from '@/lib/toastMessages';

export type ResearchType = 'note' | 'upload' | 'web_snapshot' | 'quote';

export interface SourceAttribution {
  isAuthoredByUser: boolean;
  sourceAuthor?: string;
  sourcePublication?: string;
  sourcePublicationDate?: string;
  sourcePageNumbers?: string;
  sourceDoi?: string;
  sourceIsbn?: string;
}

export interface ResearchItem {
  id: string;
  user_id: string;
  project_id: string | null;
  title: string;
  type: string;
  content: string | null;
  source_url: string | null;
  file_path: string | null;
  metadata: Record<string, unknown>;
  shared_with_projects: string[];
  created_at: string;
  updated_at: string;
  is_authored_by_user: boolean;
  source_author: string | null;
  source_publication: string | null;
  source_publication_date: string | null;
  source_page_numbers: string | null;
  source_doi: string | null;
  source_isbn: string | null;
}

function mapResearchItem(item: unknown): ResearchItem {
  const data = item as ResearchItem;
  return {
    ...data,
    metadata: (data.metadata as Record<string, unknown>) || {},
    shared_with_projects: (data.shared_with_projects as string[]) || [],
  };
}

async function fetchResearchFromDb(userId: string, projectId?: string): Promise<ResearchItem[]> {
  let query = supabase
    .from('research_items')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (projectId) {
    query = query.or(`project_id.eq.${projectId},shared_with_projects.cs.{${projectId}}`);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map(mapResearchItem);
}

export function useResearch(projectId?: string) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const queryKey = ['research', user?.id, projectId];

  const { data: items = [], isLoading: loading } = useQuery({
    queryKey,
    queryFn: () => fetchResearchFromDb(user!.id, projectId),
    enabled: !!user,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Subscribe to realtime updates
  useEffect(() => {
    if (!user) return;

    const channelId = `research-changes-${crypto.randomUUID()}`;
    const channel = supabase
      .channel(channelId)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'research_items',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient, queryKey]);

  const embedResearchItem = async (researchItemId: string) => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) return;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/embed-research`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ researchItemId }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log(`Embedded research item: ${result.chunksCreated} chunks`);
      }
    } catch (err) {
      console.error('Embedding failed:', err);
    }
  };

  // Error types for fetch web content
  type FetchErrorType = 'timeout' | 'network' | 'invalid_url' | 'server_4xx' | 'server_5xx' | 'cancelled' | 'unknown';
  
  interface FetchWebError extends Error {
    errorType: FetchErrorType;
    statusCode?: number;
    shouldRetry: boolean;
  }

  const createFetchError = (message: string, errorType: FetchErrorType, statusCode?: number): FetchWebError => {
    const error = new Error(message) as FetchWebError;
    error.errorType = errorType;
    error.statusCode = statusCode;
    error.shouldRetry = ['timeout', 'network', 'server_5xx'].includes(errorType);
    return error;
  };

  // Mutations
  const createNoteMutation = useMutation({
    mutationFn: async ({ title, content, targetProjectId, sourceAttribution }: {
      title: string;
      content: string;
      targetProjectId?: string;
      sourceAttribution?: SourceAttribution;
    }) => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('research_items')
        .insert({
          user_id: user.id,
          project_id: targetProjectId || null,
          title,
          type: 'note',
          content,
          metadata: {},
          is_authored_by_user: sourceAttribution?.isAuthoredByUser ?? true,
          source_author: sourceAttribution?.sourceAuthor || null,
          source_publication: sourceAttribution?.sourcePublication || null,
          source_publication_date: sourceAttribution?.sourcePublicationDate || null,
          source_page_numbers: sourceAttribution?.sourcePageNumbers || null,
          source_doi: sourceAttribution?.sourceDoi || null,
          source_isbn: sourceAttribution?.sourceIsbn || null,
        })
        .select()
        .single();

      if (error) throw error;
      return mapResearchItem(data);
    },
    onSuccess: (newItem) => {
      queryClient.setQueryData<ResearchItem[]>(queryKey, (old = []) => [newItem, ...old]);
      embedResearchItem(newItem.id);
    },
  });

  const createQuoteMutation = useMutation({
    mutationFn: async ({ quote, source, targetProjectId, sourceAttribution }: {
      quote: string;
      source: string;
      targetProjectId?: string;
      sourceAttribution?: SourceAttribution;
    }) => {
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('research_items')
        .insert({
          user_id: user.id,
          project_id: targetProjectId || null,
          title: `Quote from ${source}`,
          type: 'quote',
          content: quote,
          source_url: null,
          metadata: { source },
          is_authored_by_user: sourceAttribution?.isAuthoredByUser ?? false,
          source_author: sourceAttribution?.sourceAuthor || source,
          source_publication: sourceAttribution?.sourcePublication || null,
          source_publication_date: sourceAttribution?.sourcePublicationDate || null,
          source_page_numbers: sourceAttribution?.sourcePageNumbers || null,
          source_doi: sourceAttribution?.sourceDoi || null,
          source_isbn: sourceAttribution?.sourceIsbn || null,
        })
        .select()
        .single();

      if (error) throw error;
      return mapResearchItem(data);
    },
    onSuccess: (newItem) => {
      queryClient.setQueryData<ResearchItem[]>(queryKey, (old = []) => [newItem, ...old]);
      embedResearchItem(newItem.id);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const item = items.find(i => i.id === id);
      
      if (item?.file_path) {
        await supabase.storage.from('research-files').remove([item.file_path]);
      }

      const { error } = await supabase
        .from('research_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { id, item };
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ResearchItem[]>(queryKey);
      queryClient.setQueryData<ResearchItem[]>(queryKey, (old = []) => old.filter(i => i.id !== id));
      return { previous };
    },
    onError: (_, __, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Pick<ResearchItem, 'title' | 'content'>> }) => {
      const { data, error } = await supabase
        .from('research_items')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      if (updates.content && data) {
        embedResearchItem(data.id);
      }
      
      return mapResearchItem(data);
    },
    onMutate: async ({ id, updates }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ResearchItem[]>(queryKey);
      
      queryClient.setQueryData<ResearchItem[]>(queryKey, (old = []) =>
        old.map(item => item.id === id ? { ...item, ...updates } : item)
      );
      
      return { previous };
    },
    onError: (_, __, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
  });

  const shareMutation = useMutation({
    mutationFn: async ({ itemId, targetProjectId }: { itemId: string; targetProjectId: string }): Promise<{ itemId: string; newShared: string[] }> => {
      const item = items.find(i => i.id === itemId);
      if (!item) throw new Error('Item not found');

      const currentShared = item.shared_with_projects || [];
      if (currentShared.includes(targetProjectId)) {
        return { itemId, newShared: currentShared };
      }

      const newShared = [...currentShared, targetProjectId];
      
      const { error } = await supabase
        .from('research_items')
        .update({ shared_with_projects: newShared })
        .eq('id', itemId);

      if (error) throw error;
      return { itemId, newShared };
    },
    onSuccess: ({ itemId, newShared }) => {
      queryClient.setQueryData<ResearchItem[]>(queryKey, (old = []) =>
        old.map(i => i.id === itemId ? { ...i, shared_with_projects: newShared } : i)
      );
    },
  });

  const unshareMutation = useMutation({
    mutationFn: async ({ itemId, targetProjectId }: { itemId: string; targetProjectId: string }) => {
      const item = items.find(i => i.id === itemId);
      if (!item) throw new Error('Item not found');

      const newShared = (item.shared_with_projects || []).filter(id => id !== targetProjectId);
      
      const { error } = await supabase
        .from('research_items')
        .update({ shared_with_projects: newShared })
        .eq('id', itemId);

      if (error) throw error;
      return { itemId, newShared };
    },
    onSuccess: ({ itemId, newShared }) => {
      queryClient.setQueryData<ResearchItem[]>(queryKey, (old = []) =>
        old.map(i => i.id === itemId ? { ...i, shared_with_projects: newShared } : i)
      );
    },
  });

  // Helper functions for document/image extraction
  const extractDocumentText = async (filePath: string, researchItemId: string, fileType: string) => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) return;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/extract-document-text`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ filePath, researchItemId, fileType }),
        }
      );

      if (response.ok) {
        embedResearchItem(researchItemId);
        queryClient.invalidateQueries({ queryKey });
      }
    } catch (err) {
      console.error('Document extraction failed:', err);
    }
  };

  const extractImageText = async (filePath: string, researchItemId: string, fileType: string) => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) return;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/extract-image-text`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ filePath, researchItemId, fileType }),
        }
      );

      if (response.ok) {
        embedResearchItem(researchItemId);
        queryClient.invalidateQueries({ queryKey });
      }
    } catch (err) {
      console.error('Image OCR failed:', err);
    }
  };

  // Wrapper functions to maintain existing API
  const createNote = async (
    title: string, 
    content: string, 
    targetProjectId?: string,
    sourceAttribution?: SourceAttribution
  ) => {
    return createNoteMutation.mutateAsync({ title, content, targetProjectId, sourceAttribution });
  };

  const fetchWebContent = async (url: string, targetProjectId?: string, signal?: AbortSignal) => {
    if (!user) throw createFetchError('Not authenticated', 'unknown');

    const trimmedUrl = url.trim();
    if (!trimmedUrl) {
      throw createFetchError('Please enter a URL', 'invalid_url');
    }

    let normalizedUrl = trimmedUrl;
    if (!trimmedUrl.startsWith('http://') && !trimmedUrl.startsWith('https://')) {
      normalizedUrl = `https://${trimmedUrl}`;
    }

    try {
      new URL(normalizedUrl);
    } catch {
      throw createFetchError('Invalid URL format. Please enter a valid URL (e.g., https://example.com)', 'invalid_url');
    }

    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;

    if (!token) {
      throw createFetchError('Session expired. Please refresh the page and try again.', 'unknown');
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);
    
    if (signal) {
      signal.addEventListener('abort', () => controller.abort());
    }

    let response: Response;
    try {
      response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fetch-web-content`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ url: normalizedUrl }),
          signal: controller.signal,
        }
      );
    } catch (networkError) {
      clearTimeout(timeoutId);
      if (networkError instanceof Error) {
        if (networkError.name === 'AbortError') {
          if (signal?.aborted) {
            throw createFetchError('Request cancelled', 'cancelled');
          }
          throw createFetchError('Request timed out after 30 seconds.', 'timeout');
        }
      }
      throw createFetchError('Network error. Please check your internet connection.', 'network');
    } finally {
      clearTimeout(timeoutId);
    }

    let responseData;
    try {
      responseData = await response.json();
    } catch {
      const errorType: FetchErrorType = response.status >= 500 ? 'server_5xx' : 'server_4xx';
      throw createFetchError(`Server error (${response.status}).`, errorType, response.status);
    }

    if (!response.ok) {
      const errorType: FetchErrorType = response.status >= 500 ? 'server_5xx' : 'server_4xx';
      throw createFetchError(responseData.error || 'Could not fetch this URL.', errorType, response.status);
    }

    const { data, error } = await supabase
      .from('research_items')
      .insert({
        user_id: user.id,
        project_id: targetProjectId || null,
        title: responseData.title,
        type: 'web_snapshot',
        content: responseData.content,
        source_url: responseData.source_url,
        metadata: {
          description: responseData.description,
          fetched_at: responseData.fetched_at,
        },
      })
      .select()
      .single();

    if (error) throw error;

    const newItem = mapResearchItem(data);
    queryClient.setQueryData<ResearchItem[]>(queryKey, (old = []) => [newItem, ...old]);
    embedResearchItem(data.id);
    
    return data;
  };

  const uploadFile = async (
    file: File, 
    targetProjectId?: string,
    sourceAttribution?: SourceAttribution
  ) => {
    if (!user) throw new Error('Not authenticated');

    const fileExt = file.name.split('.').pop()?.toLowerCase();
    const filePath = `${user.id}/${crypto.randomUUID()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('research-files')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    let content = '';
    const isPdf = file.type === 'application/pdf' || fileExt === 'pdf';
    const isDocx = file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || fileExt === 'docx';
    const isDoc = file.type === 'application/msword' || fileExt === 'doc';
    const isImage = file.type.startsWith('image/') || ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp'].includes(fileExt || '');
    const needsDocExtraction = isPdf || isDocx || isDoc;
    const needsOcrExtraction = isImage;
    
    if (file.type === 'text/plain' || file.type === 'text/markdown' || fileExt === 'txt' || fileExt === 'md') {
      content = await file.text();
    } else if (isPdf) {
      content = `[PDF: ${file.name}]\nExtracting text content...`;
    } else if (isDocx || isDoc) {
      content = `[Word Document: ${file.name}]\nExtracting text content...`;
    } else if (isImage) {
      content = `[Image: ${file.name}]\nExtracting text via OCR...`;
    } else {
      content = `[File: ${file.name}]\nType: ${file.type}\nSize: ${(file.size / 1024).toFixed(2)} KB`;
    }

    const { data, error } = await supabase
      .from('research_items')
      .insert({
        user_id: user.id,
        project_id: targetProjectId || null,
        title: file.name,
        type: 'upload',
        content,
        file_path: filePath,
        metadata: {
          file_type: file.type,
          file_size: file.size,
          original_name: file.name,
          processing: needsDocExtraction || needsOcrExtraction,
        },
        is_authored_by_user: sourceAttribution?.isAuthoredByUser ?? false,
        source_author: sourceAttribution?.sourceAuthor || null,
        source_publication: sourceAttribution?.sourcePublication || null,
        source_publication_date: sourceAttribution?.sourcePublicationDate || null,
        source_page_numbers: sourceAttribution?.sourcePageNumbers || null,
        source_doi: sourceAttribution?.sourceDoi || null,
        source_isbn: sourceAttribution?.sourceIsbn || null,
      })
      .select()
      .single();

    if (error) throw error;

    const newItem = mapResearchItem(data);
    queryClient.setQueryData<ResearchItem[]>(queryKey, (old = []) => [newItem, ...old]);

    if (needsDocExtraction) {
      extractDocumentText(filePath, data.id, file.type);
    } else if (needsOcrExtraction) {
      extractImageText(filePath, data.id, file.type);
    }

    return data;
  };

  const createQuote = async (
    quote: string, 
    source: string, 
    targetProjectId?: string,
    sourceAttribution?: SourceAttribution
  ) => {
    return createQuoteMutation.mutateAsync({ quote, source, targetProjectId, sourceAttribution });
  };

  const deleteItem = async (id: string) => {
    await deleteMutation.mutateAsync(id);
  };

  const updateItem = async (id: string, updates: Partial<Pick<ResearchItem, 'title' | 'content'>>) => {
    return updateMutation.mutateAsync({ id, updates });
  };

  const shareWithProject = async (itemId: string, targetProjectId: string) => {
    await shareMutation.mutateAsync({ itemId, targetProjectId });
  };

  const unshareFromProject = async (itemId: string, targetProjectId: string) => {
    await unshareMutation.mutateAsync({ itemId, targetProjectId });
  };

  return {
    items,
    loading,
    refetch: () => queryClient.invalidateQueries({ queryKey }),
    createNote,
    fetchWebContent,
    uploadFile,
    createQuote,
    deleteItem,
    updateItem,
    shareWithProject,
    unshareFromProject,
  };
}
